import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { 
  Building2, Users, FileText, Activity, Shield, 
  Search, Eye, Ban, Check, Settings, RefreshCw,
  TrendingUp, Database, Clock, ChevronRight
} from 'lucide-react';
import Navbar from '@/components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MasterAdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantDetails, setTenantDetails] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      if (!parsedUser.is_master_admin) {
        toast.error('Acesso negado. Apenas o administrador master pode acessar.');
        navigate('/');
        return;
      }
      setUser(parsedUser);
    } else {
      navigate('/login');
      return;
    }
    loadDashboardData();
  }, [navigate]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, tenantsRes] = await Promise.all([
        axios.get(`${API}/admin-master/dashboard`, getAuthHeaders()),
        axios.get(`${API}/admin-master/tenants`, getAuthHeaders())
      ]);
      setStats(statsRes.data);
      setTenants(tenantsRes.data.tenants || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      if (error.response?.status === 403) {
        toast.error('Acesso negado');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadTenantDetails = async (tenantId) => {
    try {
      const response = await axios.get(
        `${API}/admin-master/tenants/${tenantId}`,
        getAuthHeaders()
      );
      setTenantDetails(response.data);
      setSelectedTenant(tenantId);
    } catch (error) {
      toast.error('Erro ao carregar detalhes do tenant');
    }
  };

  const handleBlockTenant = async (tenantId, block) => {
    try {
      const motivo = block ? prompt('Motivo do bloqueio:') : null;
      if (block && !motivo) return;

      const endpoint = block 
        ? `${API}/admin-master/tenants/${tenantId}/block?motivo=${encodeURIComponent(motivo)}`
        : `${API}/admin-master/tenants/${tenantId}/unblock`;

      await axios.post(endpoint, {}, getAuthHeaders());
      toast.success(block ? 'Tenant bloqueado' : 'Tenant desbloqueado');
      loadDashboardData();
    } catch (error) {
      toast.error('Erro ao alterar status do tenant');
    }
  };

  const filteredTenants = tenants.filter(t => 
    t.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.slug.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F7F8] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-[#0F766E]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F7F8]">
      <Navbar />
      
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-8 h-8 text-[#0F766E]" />
            <h1 className="text-2xl font-bold text-[#1F2933]">Painel Master Admin</h1>
          </div>
          <p className="text-[#64748B]">Gerencie todas as clínicas e tenants do sistema</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
            <StatCard 
              icon={Building2} 
              label="Clínicas Ativas" 
              value={stats.active_tenants} 
              color="bg-emerald-500"
            />
            <StatCard 
              icon={Users} 
              label="Usuários Total" 
              value={stats.total_users} 
              color="bg-blue-500"
            />
            <StatCard 
              icon={Users} 
              label="Pacientes Total" 
              value={stats.total_patients} 
              color="bg-purple-500"
            />
            <StatCard 
              icon={FileText} 
              label="Receitas Total" 
              value={stats.total_prescriptions} 
              color="bg-orange-500"
            />
            <StatCard 
              icon={TrendingUp} 
              label="Receitas Hoje" 
              value={stats.prescriptions_today} 
              color="bg-green-500"
            />
            <StatCard 
              icon={Ban} 
              label="Bloqueados" 
              value={stats.blocked_tenants} 
              color="bg-red-500"
            />
          </div>
        )}

        {/* Plans Summary */}
        {stats && (
          <div className="bg-white rounded-xl shadow-sm border border-[#D1D5DB] p-6 mb-8">
            <h2 className="text-lg font-semibold text-[#1F2933] mb-4">Tenants por Plano</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(stats.tenants_by_plan || {}).map(([plan, count]) => (
                <div key={plan} className="bg-[#F4F7F8] rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-[#0F766E]">{count}</div>
                  <div className="text-sm text-[#64748B] capitalize">{plan}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tenants List */}
        <div className="bg-white rounded-xl shadow-sm border border-[#D1D5DB] p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <h2 className="text-lg font-semibold text-[#1F2933]">Clínicas Cadastradas</h2>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#64748B]" />
              <Input
                placeholder="Buscar clínica..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full md:w-64"
                data-testid="search-tenant-input"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#D1D5DB]">
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#64748B]">Clínica</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#64748B]">Slug</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#64748B]">Plano</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#64748B]">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#64748B]">Criado em</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-[#64748B]">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredTenants.map((tenant) => (
                  <tr key={tenant.id} className="border-b border-[#F4F7F8] hover:bg-[#F4F7F8]">
                    <td className="py-3 px-4">
                      <div className="font-medium text-[#1F2933]">{tenant.nome}</div>
                      <div className="text-xs text-[#64748B]">{tenant.email_admin}</div>
                    </td>
                    <td className="py-3 px-4">
                      <code className="text-sm bg-[#F4F7F8] px-2 py-1 rounded">/{tenant.slug}</code>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        tenant.plano === 'enterprise' ? 'bg-purple-100 text-purple-700' :
                        tenant.plano === 'prefeitura' ? 'bg-blue-100 text-blue-700' :
                        tenant.plano === 'clinica' ? 'bg-green-100 text-green-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {tenant.plano}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {tenant.bloqueado ? (
                        <span className="flex items-center gap-1 text-red-600 text-sm">
                          <Ban className="w-4 h-4" /> Bloqueado
                        </span>
                      ) : tenant.ativo ? (
                        <span className="flex items-center gap-1 text-green-600 text-sm">
                          <Check className="w-4 h-4" /> Ativo
                        </span>
                      ) : (
                        <span className="text-gray-500 text-sm">Inativo</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-[#64748B]">
                      {new Date(tenant.data_criacao).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => loadTenantDetails(tenant.id)}
                          data-testid={`view-tenant-${tenant.slug}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleBlockTenant(tenant.id, !tenant.bloqueado)}
                          className={tenant.bloqueado ? 'text-green-600' : 'text-red-600'}
                          data-testid={`block-tenant-${tenant.slug}`}
                        >
                          {tenant.bloqueado ? <Check className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Tenant Details Modal */}
        {selectedTenant && tenantDetails && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-[#D1D5DB]">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-[#1F2933]">
                    {tenantDetails.tenant?.nome}
                  </h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setSelectedTenant(null); setTenantDetails(null); }}
                  >
                    ✕
                  </Button>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Estatísticas */}
                <div>
                  <h3 className="text-sm font-medium text-[#64748B] mb-3">Estatísticas</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-[#F4F7F8] rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-[#0F766E]">
                        {tenantDetails.statistics?.usuarios_ativos || 0}
                      </div>
                      <div className="text-xs text-[#64748B]">Usuários</div>
                    </div>
                    <div className="bg-[#F4F7F8] rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-[#0F766E]">
                        {tenantDetails.statistics?.pacientes_total || 0}
                      </div>
                      <div className="text-xs text-[#64748B]">Pacientes</div>
                    </div>
                    <div className="bg-[#F4F7F8] rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-[#0F766E]">
                        {tenantDetails.statistics?.receitas_total || 0}
                      </div>
                      <div className="text-xs text-[#64748B]">Receitas</div>
                    </div>
                    <div className="bg-[#F4F7F8] rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-[#0F766E]">
                        {tenantDetails.statistics?.receitas_hoje || 0}
                      </div>
                      <div className="text-xs text-[#64748B]">Receitas Hoje</div>
                    </div>
                  </div>
                </div>

                {/* Informações */}
                <div>
                  <h3 className="text-sm font-medium text-[#64748B] mb-3">Informações</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#64748B]">Slug:</span>
                      <code className="bg-[#F4F7F8] px-2 py-1 rounded">
                        /{tenantDetails.tenant?.slug}
                      </code>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#64748B]">Email Admin:</span>
                      <span>{tenantDetails.tenant?.email_admin}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#64748B]">Plano:</span>
                      <span className="capitalize">{tenantDetails.tenant?.plano}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#64748B]">Status:</span>
                      <span className={tenantDetails.tenant?.bloqueado ? 'text-red-600' : 'text-green-600'}>
                        {tenantDetails.tenant?.bloqueado ? 'Bloqueado' : 'Ativo'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Ações */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      navigate(`/admin-master/support/${tenantDetails.tenant?.id}`);
                    }}
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Suporte Remoto
                  </Button>
                  <Button
                    variant={tenantDetails.tenant?.bloqueado ? "default" : "destructive"}
                    className="flex-1"
                    onClick={() => {
                      handleBlockTenant(tenantDetails.tenant?.id, !tenantDetails.tenant?.bloqueado);
                      setSelectedTenant(null);
                      setTenantDetails(null);
                    }}
                  >
                    {tenantDetails.tenant?.bloqueado ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Desbloquear
                      </>
                    ) : (
                      <>
                        <Ban className="w-4 h-4 mr-2" />
                        Bloquear
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-[#D1D5DB] p-4">
      <div className="flex items-center gap-3">
        <div className={`${color} p-2 rounded-lg`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <div className="text-xl font-bold text-[#1F2933]">{value}</div>
          <div className="text-xs text-[#64748B]">{label}</div>
        </div>
      </div>
    </div>
  );
}
